import { useEffect, useState } from "react";
import { Download, PackagePlus } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";
const STATUSES = ["requested", "label_created", "pending_label", "approved", "rejected", "completed"];

export default function AdminReturns() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [returns, setReturns] = useState(null);
  const [busy, setBusy] = useState("");

  const headers = { "X-Admin-Key": adminKey };

  const load = async (key = adminKey) => {
    try {
      const r = await api.get("/returns-admin", { headers: { "X-Admin-Key": key } });
      setReturns(r.data.returns || []);
      localStorage.setItem(KEY_STORE, key);
    } catch {
      setReturns(null);
      if (key) toast.error("Invalid admin key.");
    }
  };

  useEffect(() => { if (adminKey) load(adminKey); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/returns/${id}`, { status }, { headers });
      toast.success(`Marked ${status.replace("_", " ")}.`);
      load();
    } catch { toast.error("Could not update status."); }
  };

  const genLabel = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/returns/${id}/generate-label`, {}, { headers });
      if (r.data.ok) { toast.success("Label created and emailed to the customer."); load(); }
      else toast.error("Label creation failed — check the address with the customer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Label creation failed.");
    } finally { setBusy(""); }
  };

  const inputCls = "border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[1000px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-returns-page">
      <Seo title="Returns Admin" path="/admin/returns" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Return Requests</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">
        Approve returns, generate shipping labels, and mark store credit / refunds as completed.
      </p>

      <label className="block mb-8 max-w-sm">
        <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
        <div className="flex gap-2">
          <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
            data-testid="admin-key-input" className={`${inputCls} flex-1`} />
          <button onClick={() => load()} data-testid="admin-load-button"
            className="px-4 border border-[#1A1A1A] text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">Load</button>
        </div>
      </label>

      {returns !== null && (
        returns.length === 0 ? (
          <p className="text-sm text-[#6E6E69] font-body" data-testid="returns-empty">No return requests yet.</p>
        ) : (
          <div className="space-y-4" data-testid="returns-list">
            {returns.map((r) => (
              <div key={r.id} className="border border-[#E5E5DF] p-5" data-testid={`return-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-body text-sm font-semibold">
                      Order {r.order_number} · {r.name}
                      <span className="ml-2 text-[0.6rem] font-mono tracking-[0.15em] uppercase px-2 py-0.5 border border-[#B84A3B] text-[#B84A3B]">{r.status.replace("_", " ")}</span>
                    </div>
                    <div className="text-xs text-[#6E6E69] font-body mt-1.5 space-y-0.5">
                      <div>{r.email} · {r.phone}</div>
                      <div>Returning: {r.items}{r.reason ? ` — ${r.reason}` : ""}</div>
                      <div>Wants: <b>{r.resolution === "store_credit" ? "Store credit" : "Refund to card"}</b> (minus $5 label fee)</div>
                      <div>From: {r.street1}{r.street2 ? ` ${r.street2}` : ""}, {r.city}, {r.state} {r.zip_code}</div>
                      {r.tracking_number && <div>USPS tracking: <b>{r.tracking_number}</b></div>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {r.status === "label_created" || r.label_url ? (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/returns/${r.id}/label`} target="_blank" rel="noreferrer"
                        data-testid={`label-${r.id}`}
                        className="inline-flex items-center gap-2 border border-[#1A1A1A] px-4 py-2 text-[0.62rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">
                        <Download size={12} /> Label
                      </a>
                    ) : (
                      <button onClick={() => genLabel(r.id)} disabled={busy === r.id} data-testid={`gen-label-${r.id}`}
                        className="inline-flex items-center gap-2 border border-[#1A1A1A] px-4 py-2 text-[0.62rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-50">
                        <PackagePlus size={12} /> {busy === r.id ? "Creating…" : "Accept & Send Label"}
                      </button>
                    )}
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`status-${r.id}`}
                      className="border border-[#E5E5DF] px-2 py-1.5 text-xs font-body">
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </main>
  );
}
